import Handsontable from 'handsontable/base';

type HistoryStackEntry = unknown;

export type HyperFormulaUndoRedoStacks = {
    undoStack: HistoryStackEntry[];
    redoStack: HistoryStackEntry[];
};

type HyperFormulaEngineWithUndoRedoStacks = {
    _crudOperations?: {
        undoRedo?: HyperFormulaUndoRedoStacks;
    };
};

const replaceStackContents = (baseStack: unknown[], incomingStack: unknown[]) => {
    baseStack.splice(0, baseStack.length, ...incomingStack);
};

/**
 * Returns HyperFormula's private undo/redo stacks so custom HOT actions can avoid polluting them.
 */
const getHyperFormulaUndoRedoStacks = (hotInstance: Handsontable): HyperFormulaUndoRedoStacks | undefined => {
    const formulasPlugin = hotInstance.getPlugin<'formulas'>('formulas');
    const engine = formulasPlugin?.engine as unknown as HyperFormulaEngineWithUndoRedoStacks | null | undefined;

    return engine?._crudOperations?.undoRedo;
};

/**
 * Captures HyperFormula history before custom Milanote actions mutate sheet content outside native HOT undo/redo.
 */
export const captureHyperFormulaUndoRedoStack = (hotInstance: Handsontable): HyperFormulaUndoRedoStacks | null => {
    const undoRedoStacks = getHyperFormulaUndoRedoStacks(hotInstance);
    if (!undoRedoStacks) return null;

    return {
        undoStack: [...undoRedoStacks.undoStack],
        redoStack: [...undoRedoStacks.redoStack],
    };
};

/**
 * Restores HyperFormula history while keeping the current sheet data written by Milanote's snapshot replay.
 */
export const restoreHyperFormulaUndoRedoStack = (
    hotInstance: Handsontable,
    snapshot: HyperFormulaUndoRedoStacks | null,
): void => {
    if (!snapshot) return;

    const undoRedoStacks = getHyperFormulaUndoRedoStacks(hotInstance);
    if (!undoRedoStacks) return;

    replaceStackContents(undoRedoStacks.undoStack, snapshot.undoStack);
    replaceStackContents(undoRedoStacks.redoStack, snapshot.redoStack);
};

/**
 * Runs HyperFormula housekeeping while preserving the user-facing undo/redo history.
 */
export const runWithoutChangingHyperFormulaUndoRedoStack = (hotInstance: Handsontable, callback: () => void): void => {
    const snapshot = captureHyperFormulaUndoRedoStack(hotInstance);

    try {
        callback();
    } finally {
        restoreHyperFormulaUndoRedoStack(hotInstance, snapshot);
    }
};
